/**
 * Explainer popover for the risk-detection rules used by the system.
 * Used on /risks (page header) and in TopRisksWidget header. Single source
 * of truth for the per-detector copy + severity thresholds; texts mirror
 * `backend/src/gudwin/core/detectors.py` (`_overdue_severity`,
 * `_approaching_severity`, `_overload_severity`).
 *
 * `critical_path_slippage` is intentionally not listed — detector is not
 * implemented yet (backlog `docs/tasks/57_critical-path-slippage-detector.md`).
 *
 * Pattern: see `docs/frontend/ui-spec.md` §5.5 (Explainer / помощник).
 */
import { useTranslation } from "react-i18next";
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type RisksHelpPopoverIconSize = "sm" | "md";

interface RisksHelpPopoverProps {
  iconSize?: RisksHelpPopoverIconSize;
}

export function RisksHelpPopover({ iconSize = "md" }: RisksHelpPopoverProps): React.ReactElement {
  const { t } = useTranslation();
  const iconClass = iconSize === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground"
          aria-label={t("risks.help.aria")}
          data-testid="risks-help-trigger"
        >
          <HelpCircle className={iconClass} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 space-y-3 text-sm"
        data-testid="risks-help-content"
      >
        <p className="text-base font-medium">{t("risks.help.title")}</p>
        <p className="text-muted-foreground">{t("risks.help.intro")}</p>
        <ul className="space-y-2">
          <li>
            <strong>{t("risks.type.overdue_task")}</strong> —{" "}
            {t("risks.help.detectors.overdue_task")}
          </li>
          <li>
            <strong>{t("risks.type.approaching_deadline")}</strong> —{" "}
            {t("risks.help.detectors.approaching_deadline")}
          </li>
          <li>
            <strong>{t("risks.type.resource_overload")}</strong> —{" "}
            {t("risks.help.detectors.resource_overload")}
          </li>
          <li>
            <strong>{t("risks.type.dangling_dependency")}</strong> —{" "}
            {t("risks.help.detectors.dangling_dependency")}
          </li>
          {/* critical_path_slippage намеренно не показываем — детектор не реализован, см. backlog 57 */}
        </ul>
        <p className="text-muted-foreground text-xs">{t("risks.help.refresh")}</p>
        <p className="text-muted-foreground text-xs">{t("risks.help.usage")}</p>
      </PopoverContent>
    </Popover>
  );
}
